/**
 * =============================================================================
 * CONSOLIDATED API SCHEMAS EXPORT
 * =============================================================================
 *
 * This file re-exports all API schemas from separate endpoint schema files
 * to maintain backward compatibility and provide a single import point.
 */

export * from "./createRichMenuTabGroup.schemas";
export * from "./deleteRichMenuTabImage.schemas";
// Re-export from individual endpoint schema files
export * from "./getRichMenuTemplates.schemas";
export * from "./publishRichMenuTabGroup.schemas";

// Legacy exports for backward compatibility (will be deprecated)
import { z } from "zod";
import {
	createRichMenuTabGroup200Schema,
	createRichMenuTabGroup400Schema,
	createRichMenuTabGroup500Schema,
	createRichMenuTabGroupMutationRequestSchema,
} from "./createRichMenuTabGroup.schemas";
import {
	deleteRichMenuTabImage200Schema,
	deleteRichMenuTabImage400Schema,
	deleteRichMenuTabImage404Schema,
	deleteRichMenuTabImage500Schema,
	deleteRichMenuTabImageMutationResponseSchema,
	deleteRichMenuTabImagePathParamsSchema,
} from "./deleteRichMenuTabImage.schemas";
// Import schemas from separate files for backward compatibility aliases
import {
	getRichMenuTemplates200Schema,
	getRichMenuTemplates404Schema,
	getRichMenuTemplates500Schema,
} from "./getRichMenuTemplates.schemas";
import {
	publishRichMenuTabGroup200Schema,
	publishRichMenuTabGroup400Schema,
	publishRichMenuTabGroup404Schema,
	publishRichMenuTabGroup500Schema,
	publishRichMenuTabGroupPathParamsSchema,
} from "./publishRichMenuTabGroup.schemas";
import {
	richMenuDisplayPrioritySchema,
	richMenuSchema,
	richMenuTabGroupPostUnpublishSchema,
	richMenuTabGroupSchema,
} from "./richmenu.schemas";

/**
 * =============================================================================
 * COMMON SCHEMAS
 * =============================================================================
 */

/**
 * Standard API success response base structure
 */
export const ApiSuccessResponseBaseSchema = z.object({
	message: z.string(),
	success: z.literal(true),
});

/**
 * Standard API error response base structure
 */
export const ApiErrorResponseBaseSchema = z.object({
	message: z.string(),
	success: z.literal(false),
	details: z.any().optional(),
});

/**
 * =============================================================================
 * NAMING CONVENTION ALIASES
 * =============================================================================
 */

// Rich Menu Templates API - Naming convention aliases
export const GetRichMenuTemplates200ResponseSchema =
	getRichMenuTemplates200Schema;
export const GetRichMenuTemplates404ResponseSchema =
	getRichMenuTemplates404Schema;
export const GetRichMenuTemplates500ResponseSchema =
	getRichMenuTemplates500Schema;

// Create Rich Menu Tab Group API - Naming convention aliases
export const CreateRichMenuTabGroupRequestSchema =
	createRichMenuTabGroupMutationRequestSchema;
export const CreateRichMenuTabGroup200ResponseSchema =
	createRichMenuTabGroup200Schema;
export const CreateRichMenuTabGroup400ResponseSchema =
	createRichMenuTabGroup400Schema;
export const CreateRichMenuTabGroup500ResponseSchema =
	createRichMenuTabGroup500Schema;

// Publish Rich Menu Tab Group API - Naming convention aliases
export const PublishRichMenuTabGroupPathParamsSchema =
	publishRichMenuTabGroupPathParamsSchema;
export const PublishRichMenuTabGroup200ResponseSchema =
	publishRichMenuTabGroup200Schema;
export const PublishRichMenuTabGroup400ResponseSchema =
	publishRichMenuTabGroup400Schema;
export const PublishRichMenuTabGroup404ResponseSchema =
	publishRichMenuTabGroup404Schema;
export const PublishRichMenuTabGroup500ResponseSchema =
	publishRichMenuTabGroup500Schema;

// Delete Rich Menu Tab Image API - Naming convention aliases
export const DeleteRichMenuTabImagePathParamsSchema =
	deleteRichMenuTabImagePathParamsSchema;
export const DeleteRichMenuTabImage200ResponseSchema =
	deleteRichMenuTabImage200Schema;
export const DeleteRichMenuTabImage400ResponseSchema =
	deleteRichMenuTabImage400Schema;
export const DeleteRichMenuTabImage404ResponseSchema =
	deleteRichMenuTabImage404Schema;
export const DeleteRichMenuTabImage500ResponseSchema =
	deleteRichMenuTabImage500Schema;
export const DeleteRichMenuTabImageMutationResponseSchema =
	deleteRichMenuTabImageMutationResponseSchema;

/**
 * =============================================================================
 * RICH MENU API SCHEMAS
 * =============================================================================
 */

/**
 * GET /richmenus/:id - Path Parameters
 */
export const GetRichMenuPathParamsSchema = z.object({
	id: z.string(),
});

/**
 * GET /richmenus/:id - Success Response (200)
 */
export const GetRichMenu200ResponseSchema = ApiSuccessResponseBaseSchema.extend(
	{
		data: richMenuSchema,
	},
).strict();

/**
 * GET /richmenus/:id - Not Found Response (404)
 */
export const GetRichMenu404ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * GET /richmenus/:id - Server Error Response (500)
 */
export const GetRichMenu500ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * POST /richmenus/upload-image - Success Response (200)
 */
export const UploadRichMenuImage200ResponseSchema =
	ApiSuccessResponseBaseSchema.extend({
		data: z.object({
			fileName: z.string(),
			path: z.string(),
			size: z.number(),
			mimeType: z.string(),
		}),
	}).strict();

/**
 * POST /richmenus/upload-image - Bad Request Response (400)
 */
export const UploadRichMenuImage400ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * POST /richmenus/upload-image - Server Error Response (500)
 */
export const UploadRichMenuImage500ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * PUT /richmenus/:id/image - Path Parameters
 */
export const SetRichMenuImagePathParamsSchema = z.object({
	id: z.string(),
});

/**
 * PUT /richmenus/:id/image - Success Response (200)
 */
export const SetRichMenuImage200ResponseSchema =
	ApiSuccessResponseBaseSchema.extend({
		data: z.object({
			id: z.number(),
			imageName: z.string(),
			updatedAt: z.string(),
		}),
	}).strict();

/**
 * PUT /richmenus/:id/image - Bad Request Response (400)
 */
export const SetRichMenuImage400ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * PUT /richmenus/:id/image - Not Found Response (404)
 */
export const SetRichMenuImage404ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * PUT /richmenus/:id/image - Server Error Response (500)
 */
export const SetRichMenuImage500ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * GET /richmenu-tab-groups - Query Parameters
 */
export const GetRichMenuTabGroupsQueryParamsSchema = z
	.object({
		includeDeleted: z.coerce.boolean().optional(),
	})
	.strict()
	.optional();

/**
 * GET /richmenu-tab-groups - Success Response (200)
 */
export const GetRichMenuTabGroups200ResponseSchema =
	ApiSuccessResponseBaseSchema.extend({
		data: z.array(
			richMenuTabGroupSchema.extend({
				richmenus: z.array(richMenuSchema),
			}),
		),
	}).strict();

/**
 * GET /richmenu-tab-groups - Server Error Response (500)
 */
export const GetRichMenuTabGroups500ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * GET /richmenu-tab-groups/:tabGroupId - Path Parameters
 */
export const GetRichMenuTabGroupPathParamsSchema = z.object({
	tabGroupId: z.string(),
});

/**
 * GET /richmenu-tab-groups/:tabGroupId - Success Response (200)
 */
export const GetRichMenuTabGroup200ResponseSchema =
	ApiSuccessResponseBaseSchema.extend({
		data: richMenuTabGroupSchema.extend({
			richmenus: z.array(richMenuSchema),
		}),
	}).strict();

/**
 * GET /richmenu-tab-groups/:tabGroupId - Not Found Response (404)
 */
export const GetRichMenuTabGroup404ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * GET /richmenu-tab-groups/:tabGroupId - Server Error Response (500)
 */
export const GetRichMenuTabGroup500ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * PUT /richmenu-tab-groups/:tabGroupId - Path Parameters
 */
export const UpdateRichMenuTabGroupPathParamsSchema = z.object({
	tabGroupId: z.string(),
});

/**
 * PUT /richmenu-tab-groups/:tabGroupId - Request Body
 */
export const UpdateRichMenuTabGroupRequestSchema = z
	.object({
		name: z.string().optional(),
		displayPriority: richMenuDisplayPrioritySchema.optional(),
		richmenus: z
			.array(
				richMenuSchema.partial().required({
					size: true,
					areas: true,
				}),
			)
			.optional(),
	})
	.strict();

/**
 * PUT /richmenu-tab-groups/:tabGroupId - Success Response (200)
 */
export const UpdateRichMenuTabGroup200ResponseSchema =
	ApiSuccessResponseBaseSchema.extend({
		data: richMenuTabGroupSchema.extend({
			richmenus: z.array(richMenuSchema),
		}),
	}).strict();

/**
 * PUT /richmenu-tab-groups/:tabGroupId - Not Found Response (404)
 */
export const UpdateRichMenuTabGroup404ResponseSchema =
	ApiErrorResponseBaseSchema;

/**
 * PUT /richmenu-tab-groups/:tabGroupId - Server Error Response (500)
 */
export const UpdateRichMenuTabGroup500ResponseSchema =
	ApiErrorResponseBaseSchema;

/**
 * DELETE /richmenu-tab-groups/:tabGroupId - Path Parameters
 */
export const DeleteRichMenuTabGroupPathParamsSchema = z.object({
	tabGroupId: z.string(),
});

/**
 * DELETE /richmenu-tab-groups/:tabGroupId - Success Response (200)
 */
export const DeleteRichMenuTabGroup200ResponseSchema =
	ApiSuccessResponseBaseSchema;

/**
 * DELETE /richmenu-tab-groups/:tabGroupId - Bad Request Response (400)
 */
export const DeleteRichMenuTabGroup400ResponseSchema =
	ApiErrorResponseBaseSchema;

/**
 * DELETE /richmenu-tab-groups/:tabGroupId - Not Found Response (404)
 */
export const DeleteRichMenuTabGroup404ResponseSchema =
	ApiErrorResponseBaseSchema;

/**
 * DELETE /richmenu-tab-groups/:tabGroupId - Server Error Response (500)
 */
export const DeleteRichMenuTabGroup500ResponseSchema =
	ApiErrorResponseBaseSchema;

/**
 * PUT /richmenu-tab-groups/:tabGroupId/unpublish - Path Parameters
 */
export const UnpublishRichMenuTabGroupPathParamsSchema = z.object({
	tabGroupId: z.string(),
});

/**
 * PUT /richmenu-tab-groups/:tabGroupId/unpublish - Success Response (200)
 */
export const UnpublishRichMenuTabGroup200ResponseSchema =
	ApiSuccessResponseBaseSchema.extend({
		data: richMenuTabGroupPostUnpublishSchema,
	}).strict();

/**
 * PUT /richmenu-tab-groups/:tabGroupId/unpublish - Bad Request Response (400)
 */
export const UnpublishRichMenuTabGroup400ResponseSchema =
	ApiErrorResponseBaseSchema;

/**
 * PUT /richmenu-tab-groups/:tabGroupId/unpublish - Not Found Response (404)
 */
export const UnpublishRichMenuTabGroup404ResponseSchema =
	ApiErrorResponseBaseSchema;

/**
 * PUT /richmenu-tab-groups/:tabGroupId/unpublish - Server Error Response (500)
 */
export const UnpublishRichMenuTabGroup500ResponseSchema =
	ApiErrorResponseBaseSchema;

/**
 * =============================================================================
 * BACKWARD COMPATIBILITY ALIASES (DEPRECATED)
 * =============================================================================
 */

// @deprecated Use getRichMenuTemplates200Schema instead
export const getRichMenuTemplatesResponseSchema = getRichMenuTemplates200Schema;

// @deprecated Use GetRichMenu200ResponseSchema instead
export const getRichMenuResponseSchema = GetRichMenu200ResponseSchema;

// @deprecated Use SetRichMenuImage200ResponseSchema instead
export const updateRichMenuImageResponseSchema =
	SetRichMenuImage200ResponseSchema;

// @deprecated Use createRichMenuTabGroupMutationRequestSchema instead
export const createRichMenuTabGroupRequestSchema =
	createRichMenuTabGroupMutationRequestSchema;

// @deprecated Use createRichMenuTabGroup200Schema instead
export const createRichMenuTabGroupResponseSchema =
	createRichMenuTabGroup200Schema;

// @deprecated Use GetRichMenuTabGroupsQueryParamsSchema instead
export const getRichMenuTabGroupsRequestSchema =
	GetRichMenuTabGroupsQueryParamsSchema;

// @deprecated Use GetRichMenuTabGroups200ResponseSchema instead
export const getRichMenuTabGroupsResponseSchema =
	GetRichMenuTabGroups200ResponseSchema;

// @deprecated Use GetRichMenuTabGroup200ResponseSchema instead
export const getRichMenuTabGroupResponseSchema =
	GetRichMenuTabGroup200ResponseSchema;

// @deprecated Use publishRichMenuTabGroup200Schema instead
export const publishRichmenuTabGroupResponseSchema =
	publishRichMenuTabGroup200Schema;

// @deprecated Use UnpublishRichMenuTabGroup200ResponseSchema instead
export const unpublishRichmenuTabGroupResponseSchema =
	UnpublishRichMenuTabGroup200ResponseSchema;

// @deprecated Use UpdateRichMenuTabGroupRequestSchema instead
export const updateRichmenuTabGroupRequestSchema =
	UpdateRichMenuTabGroupRequestSchema;

// @deprecated Use UpdateRichMenuTabGroup200ResponseSchema instead
export const updateRichmenuTabGroupResponseSchema =
	UpdateRichMenuTabGroup200ResponseSchema;

// @deprecated Use ApiSuccessResponseBaseSchema or ApiErrorResponseBaseSchema instead
export const commonResponseSchema = z.object({
	message: z.string(),
	success: z.boolean(),
});
